import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ExternalLeadListParams,
  ExternalLeadStatus,
} from '@tassomutuo-backoffice/types';

const DEFAULT_LIMIT = 25;
const DEFAULT_SORT = 'createdAt:desc';

export interface ExternalLeadsParamsState {
  q: string;
  status: ExternalLeadStatus | '';
  createdFrom: string;
  createdTo: string;
  page: number;
  limit: number;
  sortBy: string;
}

export interface UseExternalLeadsParamsResult {
  state: ExternalLeadsParamsState;
  apiParams: ExternalLeadListParams;
  setQ: (value: string) => void;
  setStatus: (value: ExternalLeadStatus | '') => void;
  setCreatedFrom: (value: string) => void;
  setCreatedTo: (value: string) => void;
  setSortBy: (value: string) => void;
  setPage: (value: number) => void;
  resetFilters: () => void;
}

const buildState = (params: URLSearchParams): ExternalLeadsParamsState => ({
  q: params.get('q') ?? '',
  status: (params.get('status') as ExternalLeadStatus | null) ?? '',
  createdFrom: params.get('createdFrom') ?? '',
  createdTo: params.get('createdTo') ?? '',
  page: Math.max(1, Number(params.get('page') ?? '1')),
  limit: Math.min(
    100,
    Math.max(1, Number(params.get('limit') ?? String(DEFAULT_LIMIT)))
  ),
  sortBy: params.get('sortBy') ?? DEFAULT_SORT,
});

export function useExternalLeadsParams(): UseExternalLeadsParamsResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => buildState(searchParams), [searchParams]);

  const update = useCallback(
    (patch: Partial<ExternalLeadsParamsState>, resetPage = true) => {
      const next = new URLSearchParams(searchParams);
      const apply = (key: string, value: string | number | undefined) => {
        if (value === undefined || value === '' || value === null) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      };

      if ('q' in patch) apply('q', patch.q);
      if ('status' in patch) apply('status', patch.status);
      if ('createdFrom' in patch) apply('createdFrom', patch.createdFrom);
      if ('createdTo' in patch) apply('createdTo', patch.createdTo);
      if ('sortBy' in patch) apply('sortBy', patch.sortBy);
      if ('page' in patch) apply('page', patch.page);
      if ('limit' in patch) apply('limit', patch.limit);

      if (resetPage && !('page' in patch)) {
        next.delete('page');
      }

      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const setQ = useCallback((value: string) => update({ q: value }), [update]);
  const setStatus = useCallback(
    (value: ExternalLeadStatus | '') => update({ status: value }),
    [update]
  );
  const setCreatedFrom = useCallback(
    (value: string) => update({ createdFrom: value }),
    [update]
  );
  const setCreatedTo = useCallback(
    (value: string) => update({ createdTo: value }),
    [update]
  );
  const setSortBy = useCallback(
    (value: string) => update({ sortBy: value }),
    [update]
  );
  const setPage = useCallback(
    (value: number) => update({ page: value }, false),
    [update]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: false });
  }, [setSearchParams]);

  const apiParams = useMemo<ExternalLeadListParams>(() => {
    const params: ExternalLeadListParams = {
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
    };
    if (state.q) params.q = state.q;
    if (state.status) params.status = state.status;
    if (state.createdFrom) params.createdFrom = state.createdFrom;
    if (state.createdTo) params.createdTo = state.createdTo;
    return params;
  }, [state]);

  return {
    state,
    apiParams,
    setQ,
    setStatus,
    setCreatedFrom,
    setCreatedTo,
    setSortBy,
    setPage,
    resetFilters,
  };
}
